import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, EMAIL_PATTERN } from "../../lib/api";
import { inputClass } from "../forms/FormKit";
import { fmtDate } from "./adminConfig";
import { useAuth } from "./AuthContext";

const ROLE_HELP = { admin: "Full access, including settings, exports, archiving and team management", reviewer: "View records, download CVs, change statuses and add notes" };
const sel = "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm";
const EMAIL_NOTE = { sent: "Invitation email sent.", failed: "Invitation saved, but the email could not be sent.", not_configured: "Invitation saved, but email is not configured." };

const InviteForm = ({ onDone }) => {
  const [f, setF] = useState({ email: "", name: "", role: "reviewer" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_PATTERN.test(f.email) || f.name.trim().length < 2) return toast.error("Enter a valid name and email.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team/invitations", f);
      (data.email_status === "sent" ? toast.success : toast.warning)(EMAIL_NOTE[data.email_status] || "Invitation saved.");
      setF({ email: "", name: "", role: "reviewer" });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="rounded-md border border-slate-200 bg-white p-6" data-testid="team-invite-form">
      <h2 className="font-display text-lg font-semibold text-[#0B1220]">Invite an administrator</h2>
      <p className="mt-1 text-sm text-slate-500">Invitations expire after 72 hours and can be used once. There is no public registration.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-[1fr_1fr_180px_auto] md:items-end">
        <div><label htmlFor="inv-name" className="mb-2 block text-sm font-medium">Name</label><input id="inv-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputClass(false)} data-testid="team-invite-name-input" /></div>
        <div><label htmlFor="inv-email" className="mb-2 block text-sm font-medium">Email</label><input id="inv-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputClass(false)} data-testid="team-invite-email-input" /></div>
        <div><label htmlFor="inv-role" className="mb-2 block text-sm font-medium">Role</label><select id="inv-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={`${sel} h-12 w-full`} data-testid="team-invite-role-select"><option value="reviewer">Reviewer</option><option value="admin">Admin</option></select></div>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="team-invite-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite</button>
      </div>
      <p className="mt-3 text-xs text-slate-500">{ROLE_HELP[f.role]}.</p>
    </form>
  );
};

const UserRow = ({ u, me, onUpdate }) => {
  const locked = u.id === me.id || u.owner;
  return (
    <tr className="border-b border-slate-100 last:border-0" data-testid={`team-user-${u.email}`}>
      <td className="px-4 py-3"><span className="font-medium text-[#0B1220]">{u.name || "—"}</span><span className="block text-xs text-slate-500">{u.email}</span></td>
      <td className="px-4 py-3">
        {locked ? <span className="text-sm capitalize">{u.role}{u.owner ? " · owner" : ""}{u.id === me.id ? " · you" : ""}</span> : (
          <select aria-label={`Role for ${u.email}`} value={u.role} onChange={(e) => onUpdate(u.id, { role: e.target.value })} className={sel} data-testid={`team-role-select-${u.email}`}><option value="reviewer">Reviewer</option><option value="admin">Admin</option></select>
        )}
      </td>
      <td className="px-4 py-3 text-sm">{u.active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-500">Deactivated</span>}</td>
      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.last_login_at)}</td>
      <td className="px-4 py-3 text-right">{!locked && <button type="button" onClick={() => onUpdate(u.id, { active: !u.active })} className="text-sm font-semibold text-[#2563EB]" data-testid={`team-toggle-active-${u.email}`}>{u.active ? "Deactivate" : "Reactivate"}</button>}</td>
    </tr>
  );
};

export const TeamPanel = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/admin/team").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (id, body) => {
    try { await api.patch(`/admin/team/users/${id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/admin/team/invitations/${id}`); toast.success("Invitation revoked"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-6" data-testid="team-panel">
      <InviteForm onDone={load} />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-slate-200 bg-[#F4F6F8] text-[11px] uppercase tracking-[0.12em] text-slate-500"><tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last sign-in</th><th className="px-4 py-3" /></tr></thead>
          <tbody>{data.users.map((u) => <UserRow key={u.id} u={u} me={user} onUpdate={update} />)}</tbody>
        </table>
      </div>
      <section className="rounded-md border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-semibold text-[#0B1220]">Pending invitations</h2>
        {data.invitations.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="team-no-invitations">No pending invitations.</p> : (
          <ul className="mt-4 divide-y divide-slate-100">
            {data.invitations.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-4 py-3 text-sm" data-testid={`team-invitation-${i.email}`}>
                <span><span className="font-medium text-[#0B1220]">{i.name}</span> · {i.email} · <span className="capitalize">{i.role}</span><span className="block text-xs text-slate-500">Invited by {i.invited_by} · expires {fmtDate(i.expires_at)}</span></span>
                <button type="button" onClick={() => revoke(i.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600" data-testid={`team-revoke-${i.email}`}><X className="h-4 w-4" /> Revoke</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
